/**
 * AddTaskModal — form for creating a new task.
 *
 * Behaviour:
 *   - TM users: owner_id is auto-set to themselves; no owner picker shown.
 *   - TL users: owner picker shows only their dept members + themselves.
 *   - Admin/Super: owner picker shows all active users.
 *   - Standard time (30 or 60 min) is auto-detected from title keywords
 *     (BIT 7) — shown as a read-only hint, never editable.
 *   - On submit, inserts the task and returns the fully-joined row in the
 *     SAME round trip (insert().select().single()), then hands it to
 *     onCreated so the list can render it instantly — no refetch needed.
 *   - Double submission is prevented synchronously with a ref guard:
 *     React state (`submitting`) flushes asynchronously, so two fast
 *     clicks/Enter presses could both pass a state-only check.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../api/supabase.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { useUsers } from '../../hooks/useusers.js';
import { getStdTime, getVariance } from '../../utils/timeUtils.js';
import { ROLES, isTL, isTM, canOverrideStdTime } from '../../utils/roleUtils.js';
import { escapeHtml } from '../../utils/sanitizeHtml.js';
import { TASK_LIST_SELECT } from '../../hooks/usetasks.js';
import { STD_TIME_OPTIONS } from '../../data/constants.js';
import Modal from '../ui/Modal.jsx';
import TextInput from '../ui/TextInput.jsx';

const MAX_TITLE_LENGTH = 500;
const MAX_EVENT_LENGTH = 200;

function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return format(d, 'yyyy-MM-dd');
}

export default function AddTaskModal({ open, onClose, onCreated }) {
  const { profile } = useAuth();
  const { users } = useUsers();

  const [title, setTitle] = useState('');
  const [eventName, setEventName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [closureDate, setClosureDate] = useState(tomorrowStr);
  const [stdChoice, setStdChoice] = useState('auto');   // 'auto' | '15'…'75' | 'other'
  const [stdCustom, setStdCustom] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Synchronous re-entrancy guard (Bug: one click created two tasks).
  const submitLockRef = useRef(false);

  // Reset form whenever modal opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setEventName('');
      setPriority('medium');
      setError('');
      setOwnerId(profile?.id || '');
      setClosureDate(tomorrowStr());
      setStdChoice('auto');
      setStdCustom('');
      submitLockRef.current = false;
    }
  }, [open, profile?.id]);

  // Compute owner options based on role
  const ownerOptions = useMemo(() => {
    if (!profile) return [];

    if (isTM(profile.role)) {
      // TM can only assign to self
      return users.filter((u) => u.id === profile.id);
    }

    if (isTL(profile.role)) {
      // TL: own department members + self
      return users.filter(
        (u) =>
          u.id === profile.id ||
          (u.department === profile.department && u.role === ROLES.TM),
      );
    }

    // Admin/Super: everyone
    return users;
  }, [users, profile]);

  // Auto-detected standard time, for preview only (DB will recompute)
  const detectedStd = useMemo(() => getStdTime(title), [title]);
  const detectedVariance = useMemo(() => getVariance(title), [title]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Hard gate FIRST — refs update synchronously, state does not, so this
    // is what actually blocks the second click of a double-click.
    if (submitLockRef.current) return;

    setError('');

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Task title is required.');
      return;
    }
    if (trimmedTitle.length > MAX_TITLE_LENGTH) {
      setError(`Task title must be ${MAX_TITLE_LENGTH} characters or fewer.`);
      return;
    }
    if (!ownerId) {
      setError('Please select an owner.');
      return;
    }
    // Defense-in-depth alongside RLS: the chosen owner must be one this
    // role is allowed to assign to.
    const ownerUser = ownerOptions.find((u) => u.id === ownerId);
    if (!ownerUser) {
      setError('Selected owner is not assignable from your role.');
      return;
    }
    if (!closureDate || Number.isNaN(Date.parse(closureDate))) {
      setError('Please pick a valid closure date.');
      return;
    }
    const trimmedEvent = eventName.trim();
    if (trimmedEvent.length > MAX_EVENT_LENGTH) {
      setError(`Event name must be ${MAX_EVENT_LENGTH} characters or fewer.`);
      return;
    }

    // Resolve standard time: TL+ may override the auto-detection.
    let stdMins = getStdTime(trimmedTitle);
    let varianceMin = getVariance(trimmedTitle);
    if (allowStdOverride && stdChoice !== 'auto') {
      const chosen = stdChoice === 'other' ? Number(stdCustom) : Number(stdChoice);
      if (!Number.isInteger(chosen) || chosen < 5 || chosen > 600) {
        setError('Standard time must be a whole number between 5 and 600 minutes.');
        return;
      }
      stdMins = chosen;
      varianceMin = Math.max(1, Math.round(chosen / 2));
    }

    submitLockRef.current = true;
    setSubmitting(true);

    try {

      // Titles are rendered as rich HTML in the table; escape what was
      // typed here so "<b>" appears literally instead of becoming markup.
      const { data, error: insertError } = await supabase
        .from('tasks')
        .insert({
          title: escapeHtml(trimmedTitle),
          event_name: trimmedEvent || null,
          owner_id: ownerId,
          created_by: profile.id,
          assigned_by: ownerId === profile.id ? null : profile.id,
          status: 'todo',
          priority,
          department: ownerUser.department ?? null,
          closure_date: closureDate,
          std_minutes: stdMins,
          variance_min: varianceMin,
          total_seconds: 0,
        })
        .select(TASK_LIST_SELECT)
        .single();

      if (insertError) {
        console.error('[ORIN] Insert task failed:', insertError);
        setError(insertError.message || 'Failed to create task. Please try again.');
        return;
      }

      onCreated?.(data);
      onClose?.();
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  // Determine if the owner picker should be shown
  const showOwnerPicker = profile && !isTM(profile.role);
  // TL+ may override the auto-detected standard time
  const allowStdOverride = profile && canOverrideStdTime(profile.role);

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Add a new task"
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn" disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="add-task-form"
            className="btn-primary"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? 'Creating…' : 'Create task'}
          </button>
        </>
      }
    >
      <form
        id="add-task-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
        noValidate
      >
        <TextInput
          id="task-title"
          label="Task title"
          placeholder="e.g. Draft email invite for Sangeet guests"
          required
          maxLength={MAX_TITLE_LENGTH}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={submitting}
          hint={`Standard time: ${detectedStd}m (±${detectedVariance}m). Auto-detected from title.`}
        />

        <TextInput
          id="task-event"
          label="Event"
          placeholder="e.g. Sangeet 2026 (optional)"
          maxLength={MAX_EVENT_LENGTH}
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          disabled={submitting}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {showOwnerPicker && (
            <div>
              <label
                htmlFor="task-owner"
                className="block text-sm font-medium text-ink mb-1.5"
              >
                Assign to<span className="text-status-overdue ml-0.5">*</span>
              </label>
              <select
                id="task-owner"
                required
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink focus:outline-none focus:ring-2"
              >
                <option value="" disabled>Select owner…</option>
                {ownerOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                    {u.id === profile?.id ? ' (me)' : ''}
                    {u.department ? ` · ${u.department}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label
              htmlFor="task-priority"
              className="block text-sm font-medium text-ink mb-1.5"
            >
              Priority
            </label>
            <select
              id="task-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink focus:outline-none focus:ring-2"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Standard time — TL+ may override the auto-detection */}
          {allowStdOverride && (
            <div>
              <label
                htmlFor="task-std-time"
                className="block text-sm font-medium text-ink mb-1.5"
              >
                Standard time
              </label>
              <select
                id="task-std-time"
                value={stdChoice}
                onChange={(e) => setStdChoice(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink focus:outline-none focus:ring-2"
              >
                <option value="auto">Auto · {detectedStd}m from title</option>
                {STD_TIME_OPTIONS.map((m) => (
                  <option key={m} value={String(m)}>{m} mins</option>
                ))}
                <option value="other">Other…</option>
              </select>
              {stdChoice === 'other' && (
                <input
                  type="number"
                  min={5}
                  max={600}
                  step={5}
                  value={stdCustom}
                  onChange={(e) => setStdCustom(e.target.value)}
                  placeholder="Minutes (5–600)"
                  disabled={submitting}
                  aria-label="Custom standard time in minutes"
                  className="mt-2 w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink focus:outline-none focus:ring-2"
                />
              )}
            </div>
          )}

          <div className={showOwnerPicker && !allowStdOverride ? 'sm:col-span-2' : ''}>
            <label
              htmlFor="task-closure"
              className="block text-sm font-medium text-ink mb-1.5"
            >
              Closure date<span className="text-status-overdue ml-0.5">*</span>
            </label>
            <input
              id="task-closure"
              type="date"
              required
              value={closureDate}
              onChange={(e) => setClosureDate(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        {error && (
          <div
            className="text-sm text-status-overdue bg-red-50 border border-red-200 rounded-md px-3 py-2"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
